import { useEffect, useState } from "react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { AlertTriangle } from "lucide-react";
import { api, domaineLabel, fmtDate, statutLabel } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";

const Kpi = ({ label, value, suffix = "", testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="bk-label mb-2">{label}</div>
    <div className="font-display text-3xl font-black tracking-tighter text-slate-900">
      {value}
      <span className="text-lg font-bold text-slate-400">{suffix}</span>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [a, setA] = useState(null);
  const [risques, setRisques] = useState(null);
  const [mois, setMois] = useState(12);
  const [analyse, setAnalyse] = useState(null);
  const [analyseErreur, setAnalyseErreur] = useState(false);
  const { theme } = useTheme();
  const neutral = theme === "dark" ? "#94A3B8" : "#0F172A";

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setA(data));
    api.get("/admin/materiel/a-risque").then(({ data }) => setRisques(data)).catch(() => setRisques(null));
  }, []);

  useEffect(() => {
    setAnalyse(null);
    setAnalyseErreur(false);
    api
      .get("/admin/analyse-pannes", { params: { mois } })
      .then(({ data }) => setAnalyse(data))
      .catch(() => setAnalyseErreur(true));
  }, [mois]);

  if (!a) return <div className="text-slate-400 text-sm">Chargement…</div>;

  const domaineData = Object.entries(a.par_domaine).map(([k, v]) => ({ name: domaineLabel(k), value: v }));
  const statutData = Object.entries(a.par_statut).map(([k, v]) => ({ name: statutLabel(k), value: v }));

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="bk-label mb-2">Pilotage SAV</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Analytics</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Tickets" value={a.total_tickets} testid="kpi-total" />
        <Kpi label="Auto-résolution" value={a.taux_auto_resolution} suffix="%" testid="kpi-auto" />
        <Kpi label="Temps moyen" value={a.temps_moyen_resolution_h} suffix="h" testid="kpi-temps" />
        <Kpi label="Dont temps ouvré" value={a.temps_moyen_resolution_ouvre_h} suffix="h" testid="kpi-temps-ouvre" />
        <Kpi label="CSAT" value={a.csat_moyen} suffix="/5" testid="kpi-csat" />
      </div>

      <p className="text-xs text-slate-500 -mt-4" data-testid="note-heures-ouvrees">
        Le temps ouvré ne compte que les heures d'ouverture (du lundi au vendredi, jours fériés exclus) : soit{" "}
        {a.jours_ouvres_equivalents} journée(s) en moyenne par ticket. Les nuits et week-ends ne sont plus comptés contre
        l'équipe.
      </p>

      {a.sla_escalades.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="sla-escalades">
          <AlertTriangle size={18} className="text-red-600 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-red-800">SLA dépassé — escalade requise ({a.sla_escalades.length})</div>
            <div className="text-red-700 font-mono-bk text-xs mt-1">{a.sla_escalades.join(", ")}</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="bk-label mb-4">Volume par domaine</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={domaineData}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: neutral }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: neutral }} />
              <Tooltip contentStyle={theme === "dark" ? { background: "#101010", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } : undefined} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {domaineData.map((_, i) => (
                  <Cell key={i} fill="#005CFF" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="bk-label mb-4">Répartition par statut</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statutData}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: neutral }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: neutral }} />
              <Tooltip contentStyle={theme === "dark" ? { background: "#101010", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } : undefined} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill={neutral} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {risques?.items?.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50/60 p-5" data-testid="equipements-a-risque">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle size={18} className="text-red-600 mt-0.5 shrink-0" />
            <div>
              <div className="bk-label text-red-700">Équipements à risque — usage interne</div>
              <p className="text-sm text-slate-700 mt-1.5">
                {risques.total} équipement(s) avec au moins {risques.seuil} pannes sur {risques.mois} mois. À signaler à l'équipe pour un
                remplacement ou une reprise d'installation.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {risques.items.map((r) => (
              <div
                key={`${r.axonaut_id}-${r.materiel_id || r.designation}`}
                data-testid={`risque-row-${r.materiel_id || r.designation}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white border border-red-100 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{r.designation}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.client?.nom || "Client inconnu"} · {domaineLabel(r.domaine)}
                    {r.dernier_ticket ? ` · dernière panne ${fmtDate(r.dernier_ticket)}` : ""}
                  </div>
                  {r.motifs_recurrents?.length > 0 && (
                    <div className="text-xs text-slate-600 mt-1">
                      Motifs : {r.motifs_recurrents.map(([motif, n]) => `${motif} (${n})`).join(" · ")}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl font-black tracking-tighter text-red-700">{r.tickets}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">pannes · {r.ouverts} ouverte(s)</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="analyse-pannes">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="bk-label">Analyse des pannes par domaine</div>
            <p className="text-sm text-slate-600 mt-1.5">Motifs les plus fréquents et équipements concernés, pour anticiper les remplacements.</p>
          </div>
          <div className="flex gap-1.5">
            {[3, 6, 12].map((m) => (
              <button
                key={m}
                data-testid={`analyse-mois-${m}`}
                onClick={() => setMois(m)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mois === m ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
              >
                {m} mois
              </button>
            ))}
          </div>
        </div>

        {analyseErreur && <p className="text-sm text-red-600" data-testid="analyse-erreur">Analyse indisponible pour le moment.</p>}
        {!analyse && !analyseErreur && <p className="text-sm text-slate-400" data-testid="analyse-loading">Chargement de l'analyse…</p>}
        {analyse?.domaines.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="analyse-empty">Aucun ticket sur cette période.</p>
        )}
        <div className="grid lg:grid-cols-2 gap-4">
          {analyse?.domaines.map((bloc) => (
            <div key={bloc.domaine} data-testid={`analyse-domaine-${bloc.domaine}`} className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-sm font-bold text-slate-900">{domaineLabel(bloc.domaine)}</div>
                <div className="text-xs text-slate-500">{bloc.total} panne(s)</div>
              </div>
              <div className="space-y-3 mt-3">
                {bloc.motifs.map((m) => (
                  <div key={m.motif} data-testid={`analyse-motif-${bloc.domaine}`}>
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-700 truncate pr-2">{m.motif}</span>
                      <span className="font-mono-bk text-xs text-slate-900 shrink-0">
                        {m.total} · {m.part}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                      <div className="h-full rounded-full bg-[#005CFF] transition-all duration-500" style={{ width: `${m.part}%` }} />
                    </div>
                    {m.materiels?.length > 0 && (
                      <div className="text-xs text-slate-500 mt-1">
                        Matériels : {m.materiels.map(([designation, n]) => `${designation} (${n})`).join(" · ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="bk-label mb-3">Top pannes déclarées</div>          {a.top_pannes.length === 0 && <p className="text-sm text-slate-500">Pas encore de données.</p>}
          <div className="space-y-2">
            {a.top_pannes.map(([label, count]) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 truncate pr-3">{label}</span>
                <span className="font-mono-bk text-slate-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-2">
          <div className="bk-label mb-3">Commercial & comptes</div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Leads devis</span>
            <span className="font-mono-bk">{a.leads}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Demandes de rattachement</span>
            <span className="font-mono-bk">{a.rattachements}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
